import dayjs from 'dayjs'
import shortUUID from 'short-uuid'
import { useState } from 'react'
import { camelCase, dropRight, isNil, last } from 'lodash'
import { useDispatch } from 'react-redux'

import { checkFileSize, getImageAttributeFromFile } from 'lib/image'
import { useBucketsPresignedUrlsMutation } from 'store/api/yay/misc'
import { showErrorPopup } from 'store/actions/popup'
import { ERROR_POPUP } from 'features/GlobalErrorPopup/config'
import { MAX_IMAGE_DIMENSION, MAX_IMAGE_SIZE_BYTE } from 'config'
import { isBannedUrl } from 'lib/ban'
import { scanImage } from 'components/PostBox/helpers'
import { Attachment } from 'lib/_types'

type UploadItemType =
  | 'post'
  | 'chat_message'
  | 'chat_background'
  | 'report'
  | 'user_avatar'
  | 'user_cover'
  | 'group_cover'
  | 'group_thread_icon'
  | 'group_icon'

export const useUploadImage = () => {
  const [bucketsPresignedUrls] = useBucketsPresignedUrlsMutation()
  const dispatch = useDispatch()

  const [__isLoading, __isLoadingSet] = useState(false)

  const upload = async (files: File[], type: UploadItemType) => {
    __isLoadingSet(true)

    let _files: Array<Attachment> = []
    let error = ''

    await Promise.all(
      files.map(async (file, key) => {
        const fileNameAndExtension = getFilenameAndExtension(file)
        const { naturalWidth, naturalHeight } = await getImageAttributeFromFile(
          file
        )

        if (!checkFileSize(file)) {
          error = ERROR_POPUP.FILE_SIZE_OVER_MAX_LIMIT
          return
        }

        if (
          file.type === 'image/gif' &&
          (naturalHeight > MAX_IMAGE_DIMENSION ||
            naturalWidth > MAX_IMAGE_DIMENSION)
        ) {
          error = ERROR_POPUP.FILE_DIMENSION_OVER_MAX_LIMIT
          return
        }

        const resizedImage =
          file.type === 'image/gif'
            ? file
            : await processThumbnailImage(file, naturalWidth, naturalHeight)

        const originalAttachment: Attachment = {
          file,
          fileName: '',
          originalFileName: fileNameAndExtension?.fileName,
          originalExtension: fileNameAndExtension?.extension,
          naturalWidth,
          naturalHeight,
          isThumb: false,
        }

        const thumbnailAttachment: Attachment = {
          ...originalAttachment,
          file: resizedImage,
          isThumb: true,
        }

        const uuid = shortUUID.generate()
        const timeProcessed = dayjs()

        originalAttachment.fileName = getHashedFilename(
          timeProcessed,
          originalAttachment,
          type,
          key,
          uuid
        )
        thumbnailAttachment.fileName = getHashedFilename(
          timeProcessed,
          thumbnailAttachment,
          type,
          key,
          uuid
        )

        _files.push(originalAttachment)
        _files.push(thumbnailAttachment)
      })
    )

    if (error) {
      dispatch(showErrorPopup(error))
      __isLoadingSet(false)
      return Promise.reject()
    }

    const resPregisnedUrl = await bucketsPresignedUrls(
      _files?.map?.((x) => `file_names[]=${x?.fileName}`)?.join('&')
    )

    if (!isNil(resPregisnedUrl?.['error'])) {
      __isLoadingSet(false)
      return Promise.reject()
    }

    const resUpload = await Promise.all<Attachment>(
      _files?.map?.((x) => {
        const _pUrl = resPregisnedUrl?.['data']?.presigned_urls?.find?.(
          (p: any) => p?.filename?.match?.(x?.fileName)
        )

        // We then upload each attachment to its own presigned url.
        // `FormData` cannot be used here because we need to
        // send only a file in the request's body, no keys is allowed.
        return new Promise((resolveXhr) => {
          const xhr = new XMLHttpRequest()

          xhr.onreadystatechange = () => {
            if (xhr.readyState == 4) {
              resolveXhr({
                ...x,
                fileName: _pUrl?.filename, // Override to use `filename` from presigned URL. Making this compatible with both B2 and S3.
              })
            }
          }

          xhr.open('PUT', _pUrl?.url)
          xhr.send(x?.file)
        })
      })
    )

    __isLoadingSet(false)

    return resUpload
  }

  const isImageValidForUpload = async (file: File) => {
    if (!file) return false
    if (!isValidFormat(file.type)) return false
    if (isOverMaximumFileSize(file)) return false
    if (await isContainBannedUrl(file)) return false

    return true
  }

  const isValidFormat = (format: string) => {
    const isValid = format.match(/jpg|jpeg|png|gif/g)

    if (!isValid) {
      dispatch(showErrorPopup(ERROR_POPUP.INVALID_IMAGE_FORMAT_UPLOAD))
    }

    return isValid
  }

  const isOverMaximumFileSize = (file: File) => {
    const isOver = file.size > MAX_IMAGE_SIZE_BYTE

    if (isOver) {
      dispatch(showErrorPopup(ERROR_POPUP.FILE_SIZE_OVER_MAX_LIMIT))
    }

    return isOver
  }

  const isContainBannedUrl = async (file: File) => {
    try {
      const url = await scanImage(file)
      const isInValid = isBannedUrl(url)

      if (isInValid) {
        dispatch(showErrorPopup(ERROR_POPUP.IMAGE_CONTAIN_BANNED_URL))
      }

      return isInValid
    } catch (e) {
      if (e !== 'No QR code found') {
        console.error(e)
      }
    }
  }

  return {
    upload,
    isLoading: __isLoading,
    isImageValidForUpload,
  }
}

const processThumbnailImage = async (
  file: File,
  width: number,
  height: number
) => {
  // create an image element to prepare for canvas
  const img = new Image()
  const imgPromise = new Promise<HTMLImageElement>((resolve) => {
    img.onload = () => resolve(img)
  })
  img.src = URL.createObjectURL(file)

  // wait for image to load
  await imgPromise

  // calculate the center square crop
  const size = Math.min(width, height)
  const x = (width - size) / 2
  const y = (height - size) / 2

  // create canvas and perform crop
  const cropCanvas = document.createElement('canvas')
  cropCanvas.width = size
  cropCanvas.height = size

  const ctx = cropCanvas.getContext('2d')
  if (!ctx) {
    // error: could not get canvas context
    return file
  }

  ctx.drawImage(
    img,
    x,
    y,
    size,
    size, // source
    0,
    0,
    size,
    size // target
  )

  // determine if resizing is needed
  const MAX_SIZE = 450
  let finalCanvas = cropCanvas

  if (size > MAX_SIZE) {
    // create new canvas for resizing
    finalCanvas = document.createElement('canvas')
    finalCanvas.width = MAX_SIZE
    finalCanvas.height = MAX_SIZE

    const resizeCtx = finalCanvas.getContext('2d')
    if (!resizeCtx) {
      // error: could not get canvas context
      return file
    }

    // draw cropped image at smaller size
    resizeCtx.drawImage(
      cropCanvas,
      0,
      0,
      size,
      size, // source
      0,
      0,
      MAX_SIZE,
      MAX_SIZE // target
    )
  }

  // clean up
  URL.revokeObjectURL(img.src)

  const blob = await new Promise<Blob>((resolve) => {
    finalCanvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
      },
      'image/jpeg',
      1
    )
  })

  return blob
}

const getHashedFilename = (
  timeProcessed: dayjs.Dayjs,
  att: Attachment,
  type: UploadItemType,
  key: number,
  uuid: string
) => {
  const fullDate = timeProcessed.format('YYYY/M/D')
  const thumbnail = att.isThumb ? `thumb_` : ''
  const fileName = `${thumbnail}${uuid}_${+timeProcessed}_${key}`
  const sizes = `_size_${att.naturalWidth}x${att.naturalHeight}`
  const extension = `.${att.originalExtension}`

  const hashedFilename = `${type}/${fullDate}/${fileName}${sizes}${extension}`

  return hashedFilename
}

const getFilenameAndExtension = (file: File) => {
  const _fileName = file?.name?.split?.('.')

  return {
    fileName: camelCase(dropRight(_fileName)?.join('')),
    extension: last(_fileName),
  }
}
